/* eslint-disable react/no-unstable-nested-components */

"use client";

import type { NextPage } from "next";
import dynamic from "next/dynamic";
import { useEffect, useLayoutEffect, useState } from "react";

import { useTranslation } from "@/app/i18n/client";
import Skeleton from "@/components/global/SkeletonComponent/server/SkeletonComponent";
import { svgPaths } from "@/components/icons/svgPath";
import { homeConfig } from "@/config/home";
import pushGAHomePage from "@/libs/ga4/action/homePage";
import { useDirectFlight } from "@/libs/store/flights/useDirectFlightStore";
import type { LanguageCode } from "@/utils/common/langHelper/langUtills";
import { FeaturesTypePathEnum } from "@/utils/enums/FeaturePathGlobalEnum";

import SkeletonCarSearchPanelComponent from "../car-rentals/components/landing/search/SkeletonCarSearchPanelComponent";
import type { CarHomeSearchComponentProps } from "../car-rentals/search/components/SearchPanelComponent/CarHomeSearchComponent";
import SkeletonExperienceSearchPanelComponent from "../experiences/components/ExperienceSearchComponent/components/ExperienceSearchPanelComponent/SkeletonExperienceSearchPanelComponent";
import type { ExperienceSearchPanelComponentPropsInterface } from "../experiences/components/ExperienceSearchComponent/ExperienceSearchInterface";
import SkeletonFlightSearchComponent from "../flights/components/FlightSearchComponent/SkeletonFlightSearchComponent";
import type { HotelSearchBoxForHomeComponentProps } from "../hotels/components/HotelSearchComponent/HotelSearchBoxComponent/HotelSearchBoxForHomeComponent";
import SkeletonHotelSearchBoxComponent from "../hotels/components/HotelSearchComponent/HotelSearchBoxComponent/SkeletonHotelSearchBoxComponent";
import type { TourSearchPanelPropsInterface } from "../tour-packages/components/searchPanel/SearchPanelComponent";
import SkeletonTourSearchPanelComponent from "../tour-packages/components/searchPanel/SkeletonTourSearchPanelComponent";
import SkeletonHomeSearchBarDesktopComponent from "./SkeletonHomeSearchBarDesktopComponent";

const SvgComponent = dynamic(() => import("@/components/icons/IconGenerator"), {
  ssr: false,
  loading: () => <Skeleton className="size-6 rounded-full" />,
});

const HomeSearchBarDesktopComponent = dynamic(
  () => import("./HomeSearchBarDesktopComponent"),
  {
    ssr: false,
    loading: () => <SkeletonHomeSearchBarDesktopComponent />,
  },
);

const FlightSearchComponent = dynamic(
  () =>
    import("../flights/components/FlightSearchComponent/FlightSearchComponent"),
  {
    ssr: false,
    loading: () => <SkeletonFlightSearchComponent />,
  },
);

export interface HomeSearchInterface {
  lang: string;
  isDesktop?: boolean;
}

type LazyComponents = {
  HotelSearch: NextPage<HotelSearchBoxForHomeComponentProps> | null;
  ExperienceSearch: NextPage<ExperienceSearchPanelComponentPropsInterface> | null;
  ToursSearch: NextPage<TourSearchPanelPropsInterface> | null;
  CarSearch: NextPage<CarHomeSearchComponentProps> | null;
};

const HomeSearchDesktopComponent = ({
  lang,
  isDesktop = false,
}: HomeSearchInterface) => {
  const { t } = useTranslation("home");
  const [search, setSearch] = useState<string>(homeConfig.desktop.mainTab);
  const { getDisplayNameByFlight, responseDisplay } = useDirectFlight();
  const disableMenu = homeConfig.desktop;

  const [lazyComponents, setLazyComponents] = useState<LazyComponents>({
    HotelSearch: null,
    ExperienceSearch: null,
    CarSearch: null,
    ToursSearch: null,
  });

  const onLoadOtherSearchComponent = async () => {
    const hotel = dynamic(
      () =>
        import(
          "../hotels/components/HotelSearchComponent/HotelSearchBoxComponent/HotelSearchBoxForHomeComponent"
        ),
      { ssr: false, loading: () => <SkeletonHotelSearchBoxComponent /> },
    );

    const experience = dynamic(
      () =>
        import(
          "../experiences/components/ExperienceSearchComponent/components/ExperienceSearchPanelComponent/ExperienceSearchPanelComponent"
        ),
      { ssr: false, loading: () => <SkeletonExperienceSearchPanelComponent /> },
    );

    const car = dynamic(
      () =>
        import(
          "../car-rentals/search/components/SearchPanelComponent/CarHomeSearchComponent"
        ),
      { ssr: false, loading: () => <SkeletonCarSearchPanelComponent /> },
    );

    const tours = dynamic(
      () =>
        import("../tour-packages/components/searchPanel/SearchPanelComponent"),
      { ssr: false, loading: () => <SkeletonTourSearchPanelComponent /> },
    );

    setLazyComponents({
      HotelSearch: hotel,
      ExperienceSearch: experience,
      CarSearch: car,
      ToursSearch: tours,
    });
  };

  const handleClickMenu = (key: string) => {
    pushGAHomePage()?.ClickMenuOnSerchSection(key);
    setSearch(key);
  };

  const ItemList = [
    {
      type: FeaturesTypePathEnum.FLIGHTS,
      icon: (
        <div className="size-6 rotate-45">
          <SvgComponent
            svgIcon={svgPaths.navigator.flights}
            width={24}
            height={24}
          />
        </div>
      ),
    },
    {
      type: FeaturesTypePathEnum.HOTELS,
      icon: (
        <span className="size-6">
          <SvgComponent
            svgIcon={svgPaths.navigator.hotels}
            width={24}
            height={24}
          />
        </span>
      ),
    },
    {
      type: FeaturesTypePathEnum.EXPERIENCES,
      icon: (
        <span className="size-6">
          <SvgComponent
            svgIcon={svgPaths.homeSearchExperiences}
            width={24}
            height={24}
          />
        </span>
      ),
    },
    {
      type: FeaturesTypePathEnum.CARRENTALS,
      icon: (
        <span className="size-6">
          <SvgComponent
            svgIcon={svgPaths.navigator["car-rentals"]}
            width={24}
            height={24}
          />
        </span>
      ),
    },
    {
      type: FeaturesTypePathEnum.TOURS,
      icon: (
        <span className="size-6">
          <SvgComponent
            svgIcon={svgPaths.navigator["tour-packages"]}
            width={24}
            height={24}
          />
        </span>
      ),
    },
  ];

  useEffect(() => {
    const responseLength = responseDisplay && responseDisplay.length > 0;
    if (!responseLength) {
      getDisplayNameByFlight();
    }
  }, []);

  useLayoutEffect(() => {
    onLoadOtherSearchComponent();
  }, []);

  return (
    <section className="relative flex size-full min-h-[21rem] flex-col items-center px-[16px] md:mx-auto xl:px-0">
      {disableMenu.isHiddenMenu ? null : (
        <HomeSearchBarDesktopComponent
          t={t}
          search={search}
          ItemList={ItemList}
          disableMenu={disableMenu}
          handleClickMenu={handleClickMenu}
        />
      )}
      {search === FeaturesTypePathEnum.FLIGHTS && (
        <FlightSearchComponent isMainPage isHomePage />
      )}
      {search === FeaturesTypePathEnum.HOTELS && lazyComponents.HotelSearch && (
        <div className="w-full">
          <lazyComponents.HotelSearch
            buttonLabel={t("SearchButtonText")}
            destinationTitle={t("destination")}
            checkInTitle={t("checkIn")}
            checkOutTitle={t("checkOut")}
            roomAndGuestTitle={t("RoomsAndGuests")}
            styleCustom="rounded-[1rem] shadow-search-mobile md:shadow-search bg-white py-[1.5rem] px-[1.5rem]"
          />
        </div>
      )}
      {search === FeaturesTypePathEnum.EXPERIENCES &&
        lazyComponents.ExperienceSearch && (
          <div className="h-fit w-full">
            <lazyComponents.ExperienceSearch
              buttonText={t("SearchButtonText")}
              lang={lang as LanguageCode}
              label={t("destinationExp")}
            />
          </div>
        )}
      {search === FeaturesTypePathEnum.CARRENTALS &&
        lazyComponents.CarSearch && (
          <lazyComponents.CarSearch isDesktopServer={isDesktop} />
        )}
      {search === FeaturesTypePathEnum.TOURS && lazyComponents.ToursSearch && (
        <div className="h-fit w-full">
          <lazyComponents.ToursSearch
            buttonLabel="searchPanel.searchButtonText"
            destinationTitle="searchPanel.destinationPlaceholder"
            checkInTitle="searchPanel.checkIn"
            checkInTitleMobile="searchPanel.checkInMobile"
            checkOutTitle="searchPanel.checkOut"
            styleCustom="rounded-2xl shadow-search-mobile md:shadow-search bg-white md:p-6 p-4"
          />
        </div>
      )}
    </section>
  );
};

export default HomeSearchDesktopComponent;
